import { Button, Checkbox, TextField } from '@pms-digital/design-system';
import { type FormEvent, useId, useState } from 'react';

/**
 * The entry door of a file, shared between the Ambiance screen, the inline
 * gestures of the openings editor (BUILD-719) and the media library
 * (BUILD-831): a file, its license, free tags, one submit.
 *
 * The license is FACULTATIVE on both natures (BUILD-891, an operator's own
 * words: « ne peux avoir de Licence obligatoire a remplir mais un boolean
 * "ajouter une licence" suffit qui est décoché de base »). It lifts the
 * BUILD-831 asymmetry that kept it mandatory for a sound: requiring a
 * provenance for every recording only taught the operator to type a word to
 * get past the door, producing no real provenance and costing a gesture. The
 * checkbox is the field's own gate now - unchecked by default, revealing the
 * text input rather than forcing it open - and the submit button is closed
 * only for want of a FILE, never for want of a license.
 *
 * The form is UI only. The caller posts on the EXISTING upload route of the
 * ambiance assets and words any refusal in French from its code - nothing of
 * an asset ever transits through the openings routes (O4). Extracted from
 * `ambiance-panel.tsx` rather than copied: two copies of this door would
 * drift on the first rule change.
 *
 * ## The file control speaks French (BUILD-923)
 *
 * A bare `<input type="file">` paints two things the page does not own: a
 * button reading « Choose File » and, beside it, « No file chosen ». The
 * `file:` pseudo-element dresses the FIRST one and nothing dresses the second
 * - it is shadow content the browser writes in ITS OWN locale, so a French
 * form entirely designed down to its refusal sentences ended on two English
 * strings nobody could translate or restyle. It was the loudest inconsistency
 * in the whole administration.
 *
 * So the input is stretched over a box the page draws, at `opacity: 0`, and
 * the box says « Parcourir… » and names the picked file. Three properties are
 * load-bearing and none of them is decoration:
 *
 *  - it is still a REAL `<input type="file">` in the tree, so it keeps its
 *    accessible name from the `<label htmlFor>` above it, stays in the tab
 *    order, and `setInputFiles` / `user.upload` drive it unchanged;
 *  - it is COVERED rather than `sr-only`, which is the lesson of BUILD-863
 *    (m5) and of BUILD-916 at once. `sr-only` clips the control to one pixel
 *    somewhere under its own box, so a pointer aimed at it hits a sibling -
 *    and it is `position: absolute`, which a `position: static` scroller does
 *    not clip, i.e. one of the four documented causes of a page that scrolls
 *    sideways. `absolute inset-0` inside a `relative` box can never stick out
 *    of it;
 *  - the focus ring moves onto the BOX (`has-[:focus-visible]`): an input at
 *    zero opacity paints none of its own, so without this the control would
 *    be reachable by keyboard and invisible while focused.
 */

export interface AssetUploadFormProps {
  busy: boolean;
  /** What the native picker offers; the server stays the authority. */
  accept: string;
  /**
   * The Ambiance page clears the form on submit - its historical behavior.
   * The inline gestures keep the entry instead, so a refusal never costs the
   * operator the license they typed (BUILD-719).
   */
  resetOnSubmit?: boolean;
  /** Tighter rows for the inline context of a block card. */
  compact?: boolean;
  /** The French of the last refusal, worded from its code; null when none. */
  refusal?: string | null;
  onUpload: (file: File, license: string, tags: string) => void;
  /** Renders a cancel gesture when provided - the inline form is closable. */
  onCancel?: (() => void) | undefined;
}

export function AssetUploadForm({
  busy,
  accept,
  resetOnSubmit = false,
  compact = false,
  refusal = null,
  onUpload,
  onCancel,
}: AssetUploadFormProps) {
  const fileId = useId();
  // The line under the row is the file control's DESCRIPTION: it is what says
  // why the submit is shut, and a keyboard user who never sees the row as a
  // whole would otherwise meet a closed button with no reason attached.
  const hintId = useId();
  const [fileName, setFileName] = useState<string | null>(null);
  // Decoché par défaut (BUILD-891): the field is a gate the operator opens,
  // never a box the form starts with open. The license itself is HELD apart
  // from the checkbox so unchecking never throws away what was typed.
  const [licenseEnabled, setLicenseEnabled] = useState(false);
  const [license, setLicense] = useState('');

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    // Read the FILE from the input and not from a FormData: the input holds
    // the original File - name included - where jsdom's FormData reconstructs
    // a nameless copy, and the name is what the server derives the format from.
    const fileInput = form.elements.namedItem('file');
    const file = fileInput instanceof HTMLInputElement ? (fileInput.files?.[0] ?? null) : null;
    const tagsInput = form.elements.namedItem('tags');
    const tags = tagsInput instanceof HTMLInputElement ? tagsInput.value.trim() : '';
    if (file === null || file.name === '') return;
    // Unchecked or empty are the same "none": the checkbox decides whether
    // the text field is even read, so a licence typed then unticked never
    // reaches the caller.
    onUpload(file, licenseEnabled ? license.trim() : '', tags);
    if (resetOnSubmit) {
      form.reset();
      setFileName(null);
      setLicenseEnabled(false);
      setLicense('');
    }
  }

  // ONE height and ONE label for the three controls, so the three labels sit
  // on one baseline (BUILD-923). They did not: the file input was as tall as
  // the browser drew it, `items-end` aligned the row on its bottom, and
  // « Fichier » landed eight pixels below « Licence » and « Mots-clés ». The
  // box is the height of the package `TextField` beside it (36 px compact,
  // 44 px otherwise), and `label` its label's words.
  const label = FIELD_LABEL;
  const box = compact ? 'h-9' : 'h-11';
  return (
    <form onSubmit={submit} className="space-y-3">
      {/*
        A grid rather than `flex flex-wrap` with three arbitrary `min-w-*`:
        the widths were unrelated to what each field holds, and every cell
        carries `min-w-0` so nothing here refuses to shrink on a phone.
      */}
      <div
        className={`grid gap-x-4 ${compact ? 'gap-y-2' : 'gap-y-3'} sm:grid-cols-2 lg:grid-cols-3`}
      >
        <div className="min-w-0 space-y-2">
          <label htmlFor={fileId} className={label}>
            Fichier
          </label>
          <div
            className={`relative flex ${box} w-full min-w-0 items-center gap-2 rounded-md border-2 border-gray-300 px-2 has-[:focus-visible]:border-bleu-nuit`}
          >
            <span
              className={`shrink-0 rounded-md border-2 border-bleu-nuit px-2 ${
                compact ? 'py-0.5 text-caption' : 'py-1 text-small'
              } font-semibold text-bleu-nuit`}
            >
              Parcourir…
            </span>
            <span className="min-w-0 flex-1 truncate text-gray-700 text-small">
              {fileName ?? 'Aucun fichier choisi'}
            </span>
            <input
              id={fileId}
              name="file"
              type="file"
              // Not `required`: a file input's value stays empty in jsdom and
              // constraint validation would swallow the submit. The submit
              // button stays disabled until a file is picked instead - same
              // protection, visible instead of implicit.
              accept={accept}
              aria-describedby={hintId}
              onChange={(event) => setFileName(event.target.files?.[0]?.name ?? null)}
              // Covered, never `sr-only` - see the header. The whole control
              // is the click target, so a pointer aimed anywhere in the box
              // opens the picker.
              className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
            />
          </div>
        </div>
        <div className="min-w-0 space-y-2">
          <span className={label}>Licence</span>
          {/*
            The checkbox IS the gate (BUILD-891): unchecked by default, it
            reveals the text field rather than forcing it open. The package
            `Checkbox` makes its whole sentence the click target, the same
            reasoning as the covered file control above.
          */}
          <Checkbox
            label="Ajouter une licence"
            checked={licenseEnabled}
            onCheckedChange={setLicenseEnabled}
          />
          {licenseEnabled ? (
            <TextField
              label="Licence"
              hideLabel
              compact={compact}
              name="license"
              value={license}
              onChange={(event) => setLicense(event.target.value)}
              placeholder="CC0 - freesound.org/123456"
            />
          ) : null}
        </div>
        <div className="min-w-0">
          {/* Uncontrolled: the submit reads it off the form by its `name`. */}
          <TextField
            label="Mots-clés (facultatif)"
            compact={compact}
            name="tags"
            placeholder="gare, nuit"
          />
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <Button
          type="submit"
          variant="secondary"
          size={compact ? 'sm' : 'default'}
          disabled={busy || fileName === null}
        >
          {busy ? 'En cours…' : 'Téléverser'}
        </Button>
        {onCancel !== undefined ? (
          <Button
            type="button"
            size={compact ? 'sm' : 'default'}
            variant="accent"
            disabled={busy}
            onClick={onCancel}
          >
            Annuler
          </Button>
        ) : null}
      </div>
      {refusal === null ? null : (
        <p role="alert" className="text-gray-900 text-small">
          <strong>Refusé :</strong> {refusal}.
        </p>
      )}
      {/* The line under the row says WHY the button is closed when it is:
          a disabled control with no sentence beside it reads as a bug. */}
      <p id={hintId} className="text-gray-600 text-caption">
        {fileName === null
          ? 'Choisissez un fichier pour commencer ; la licence est facultative.'
          : `Prêt à téléverser : ${fileName}.`}
      </p>
    </form>
  );
}

const FIELD_LABEL = 'block font-normal text-noir text-small';
